/**
 * Data for the Cost Explorer.
 *
 * Built entirely from endpoints that already exist, so no backend change is
 * needed to slice spend:
 *
 *   /api/jobs          every job's result_data — pages and tokens, the things
 *                      that are actually priced — newest first
 *   /api/requests      template, creator and channel traces for those jobs
 *   /api/admin/users   which creators are people (service accounts are left
 *                      out of that list on purpose, which is the app signal)
 *
 * /api/jobs has no date filter, but it is ordered newest first, so paging stops
 * as soon as a page reaches back past the start of the period. Only completed
 * document-analysis jobs are asked for (the ones the billing ledger records).
 * Each period is cached, so flicking between tabs never re-pulls what is
 * already on screen, and late replies from an earlier period are dropped.
 *
 * `loadedFor` says which period the data on hand was actually read for: a
 * failed reload keeps the previous data (nothing blanks) but never lets it
 * pass for the new period's.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { adminApi, jobsApi, requestsApi } from '../../services/api'
import { isBillableJob, parseUtc, periodBounds } from '../../utils/costExplorer'
import { periodKey } from './useBillingData'

const JOB_PAGE_SIZE = 200
const REQUEST_PAGE_SIZE = 100
/** Safety caps; reaching one is reported to the page, never silently absorbed. */
export const MAX_JOB_PAGES = 30
export const MAX_REQUEST_PAGES = 40
/** Pages read back past the window to find older requests that jobs re-ran. */
const MAX_OLDER_PAGES = 10
const PARALLEL = 3
/**
 * Jobs are listed newest-created first but windowed by when they completed, so
 * the walk back goes a day past the start before it stops: a job created
 * shortly before the window that finished inside it must still be read.
 */
const START_MARGIN_MS = 86_400_000

function isoDate(d) {
  return d.toISOString().slice(0, 10)
}

const cacheKey = periodKey

/** Run `fn` over `items`, `limit` at a time. */
async function mapLimit(items, limit, fn) {
  const out = new Array(items.length)
  let next = 0
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next++
      out[i] = await fn(items[i], i)
    }
  })
  await Promise.all(workers)
  return out
}

/** Jobs newest-first until the page reaches back past `start`. */
export async function fetchJobsSince(start, onProgress) {
  const jobs = []
  let page = 1
  let totalPages = null
  let reachedStart = false
  let exhausted = false

  while (!reachedStart && !exhausted && page <= MAX_JOB_PAGES) {
    // Page 1 alone first: it says how many pages exist, and a short period is
    // often answered by it outright.
    const last = page === 1 ? 1 : Math.min(page + PARALLEL - 1, MAX_JOB_PAGES, totalPages ?? Infinity)
    const batch = []
    for (let p = page; p <= last; p++) batch.push(p)
    const responses = await Promise.all(batch.map(p => jobsApi.list({
      page: p, per_page: JOB_PAGE_SIZE, job_type: 'document_analysis', status: 'completed',
    })))
    for (const res of responses) {
      const rows = res?.data?.jobs || []
      if (totalPages === null && res?.data?.pagination?.pages != null) totalPages = res.data.pagination.pages
      jobs.push(...rows)
      const oldest = parseUtc(rows[rows.length - 1]?.created_at)
      if (start && oldest && oldest.getTime() < start.getTime() - START_MARGIN_MS) reachedStart = true
      if (rows.length < JOB_PAGE_SIZE) exhausted = true
    }
    onProgress?.({ phase: 'jobs', loaded: jobs.length })
    page = last + 1
    if (totalPages !== null && page > totalPages) exhausted = true
  }
  // Offset paging can hand the same job over twice when one is created while
  // the pages are being read (everything shifts down a row), so de-duplicate by
  // id. A double-counted job would be priced twice.
  const seen = new Set()
  const unique = jobs.filter(j => {
    if (j?.id == null) return true
    const id = String(j.id)
    if (seen.has(id)) return false
    seen.add(id)
    return true
  })
  return { jobs: unique.filter(j => j.result_data && isBillableJob(j)), truncated: !reachedStart && !exhausted }
}

/** Requests created inside the window, then any older ones the jobs still point at. */
export async function fetchRequestsFor(jobs, bounds, onProgress) {
  const params = { per_page: REQUEST_PAGE_SIZE }
  if (bounds.start) params.date_from = isoDate(bounds.start)
  if (bounds.end) params.date_to = isoDate(bounds.end)

  const first = await requestsApi.list({ ...params, page: 1 })
  const requests = [...(first?.data?.requests || [])]
  const pages = Math.min(first?.data?.pagination?.pages || 1, MAX_REQUEST_PAGES)
  const truncated = (first?.data?.pagination?.pages || 1) > MAX_REQUEST_PAGES
  onProgress?.({ phase: 'requests', loaded: requests.length })

  const rest = []
  for (let p = 2; p <= pages; p++) rest.push(p)
  await mapLimit(rest, PARALLEL, async p => {
    const res = await requestsApi.list({ ...params, page: p })
    requests.push(...(res?.data?.requests || []))
    onProgress?.({ phase: 'requests', loaded: requests.length })
  })

  // A job can point at a request the window's list does not hold: one created
  // before the window but re-run inside it, or one that has since been deleted
  // (its jobs, and their cost, outlive it). Older requests are found by paging
  // the list on back past the window — never one GET per id, which for deleted
  // requests is a run of 404s. Whatever is still missing once the pages reach
  // ids older than it is gone, and shows as "Request not found".
  const known = new Set(requests.map(r => String(r.id)))
  const remaining = new Set(
    jobs.filter(j => j.entity_type === 'request' && j.entity_id != null)
      .map(j => String(j.entity_id))
      .filter(id => !known.has(id)),
  )

  // Unbounded window: the list already holds every request that still exists.
  if (bounds.start && remaining.size) {
    const before = new Date(Date.UTC(
      bounds.start.getUTCFullYear(), bounds.start.getUTCMonth(), bounds.start.getUTCDate() - 1,
    ))
    const olderParams = { per_page: REQUEST_PAGE_SIZE, date_to: isoDate(before) }
    const minRemaining = () => Math.min(...[...remaining].map(Number).filter(Number.isFinite))

    for (let page = 1; page <= MAX_OLDER_PAGES && remaining.size; page++) {
      const res = await requestsApi.list({ ...olderParams, page })
      const rows = res?.data?.requests || []
      for (const r of rows) {
        if (remaining.delete(String(r.id))) requests.push(r)
      }
      onProgress?.({ phase: 'requests', loaded: requests.length })
      const oldest = Math.min(...rows.map(r => Number(r.id)).filter(Number.isFinite))
      // Newest first: once a page is older than every id still wanted, the
      // rest of the list cannot contain them.
      if (rows.length < REQUEST_PAGE_SIZE || !remaining.size || oldest < minRemaining()) break
    }
  }

  return { requests, truncated }
}

/**
 * @param range   page-wide period value
 * @param custom  { from, to } when range === 'custom', else null
 * @param enabled false until the Costs tab is first opened
 */
export function useCostExplorer(range, custom, enabled) {
  const [state, setState] = useState({ data: null, loading: false, error: null, progress: null, requestedKey: null })
  const cache = useRef(new Map())
  const seq = useRef(0)

  const load = useCallback(async ({ force = false } = {}) => {
    if (range === 'custom' && !(custom?.from && custom?.to)) return
    const key = cacheKey(range, custom)
    // Every call takes a number, cache hits included: otherwise a slower load
    // for another period that is still in flight would land after this one and
    // overwrite the period the page is actually on.
    const id = ++seq.current
    if (!force && cache.current.has(key)) {
      setState({ data: cache.current.get(key), loading: false, error: null, progress: null, requestedKey: key })
      return
    }
    const bounds = periodBounds(range, custom)
    setState(s => ({ ...s, loading: true, error: null, progress: { phase: 'jobs', loaded: 0 }, requestedKey: key }))
    const progress = p => { if (id === seq.current) setState(s => ({ ...s, progress: p })) }

    try {
      const usersPromise = adminApi.listUsers({ include_inactive: true })
        .then(res => res?.data?.users || null)
        .catch(() => null)
      const { jobs, truncated: jobsTruncated } = await fetchJobsSince(bounds.start, progress)
      if (id !== seq.current) return
      const { requests, truncated: requestsTruncated } = await fetchRequestsFor(jobs, bounds, progress)
      const users = await usersPromise
      if (id !== seq.current) return

      const data = {
        key, bounds, jobs, requests, users,
        truncated: jobsTruncated || requestsTruncated,
        loadedAt: new Date(),
      }
      cache.current.set(key, data)
      setState({ data, loading: false, error: null, progress: null, requestedKey: key })
    } catch (err) {
      if (id !== seq.current) return
      setState(s => ({ ...s, loading: false, error: err, progress: null }))
    }
  }, [range, custom])

  useEffect(() => {
    if (enabled) load()
  }, [enabled, load])

  const reload = useCallback(() => {
    if (enabled) return load({ force: true })
    cache.current.clear()
    return null
  }, [enabled, load])

  return { ...state, loadedFor: state.data?.key ?? null, reload }
}
